import type { ThemeTokens, TitleLevel } from '@course-authoring/shared-types';

export type TitleTypography = ThemeTokens['typography']['h2'];

/**
 * NA-227: content-block headings tighten their line-height once the resolved
 * font-size is large enough that `normal` (~1.4× for Source Sans 3) reads as
 * too airy. At/above 26px use 1.25; smaller sizes keep `normal` (1.25 was too
 * tight at ≤20px). Keyed on the resolved size so it covers every level ×
 * breakpoint combination without a per-level matrix.
 */
export function resolveHeadingLineHeight(size: string): string {
  const px = Number.parseFloat(size);
  return Number.isFinite(px) && px >= 26 ? '1.25' : 'normal';
}

/**
 * Shared title-typography ladder for block titles (h1–h4).
 *
 * NOTE: every current caller (Click & Expand, Stylized Sidebar Callout,
 * Stylized List, Policy Acknowledgement) switches to the mobile typography
 * scale on small viewports via `options.isMobile`.
 * Blocks needing the full three-tier content-block stepping (Basic Content
 * Block, Lesson Complete, Workplace Color Spectrum, Likert, Categorization,
 * Single Choice Answer, Comparison Slider) use
 * getContentBlockHeadingTypography below instead of this function.
 *
 * NA-227: whichever size is resolved, the line-height follows the shared
 * size-keyed rule above.
 */
export function getTitleTypography(
  tokens: ThemeTokens,
  level: TitleLevel,
  options?: { isMobile?: boolean },
): TitleTypography {
  const scale = options?.isMobile ? tokens.typography.mobile : tokens.typography;

  let base: TitleTypography;
  switch (level) {
    case 'h1':
      base = scale.h1;
      break;
    case 'h3':
      base = scale.h3;
      break;
    case 'h4':
      base = scale.h4;
      break;
    default:
      base = scale.h2;
  }

  return { ...base, lineHeight: resolveHeadingLineHeight(base.size) };
}

/**
 * NA-227: narrow-phone (≤576px) heading sizes for shared content-block headings
 * (Basic Content Block + Lesson Complete). NA-205 established the H1 (32) and
 * H4 (16) steps by eye; NA-227 adds the matching H2 (26) and H3 (20) steps.
 */
const CONTENT_BLOCK_SMALL_MOBILE_HEADING_SIZE: Record<TitleLevel, string> = {
  h1: '32px',
  h2: '26px',
  h3: '20px',
  h4: '16px',
};

/**
 * Full three-tier heading resolution for content-block titles. Shared by Basic
 * Content Block, Lesson Complete, Workplace Color Spectrum, Likert,
 * Categorization, and Single Choice Answer so their titles size identically
 * across viewports:
 *  - >768px: desktop scale
 *  - 577–768px: mobile scale, except H4 holds its 20px desktop size so it never
 *    renders smaller than the 20px body copy beside it.
 *  - ≤576px: explicit narrow-phone sizes (H1 32 / H2 26 / H3 20 / H4 16).
 * Line-height always follows the shared size-keyed rule.
 */
export function getContentBlockHeadingTypography(
  tokens: ThemeTokens,
  level: TitleLevel,
  options: { isMobile: boolean; isSmallMobile: boolean },
): TitleTypography {
  let base: TitleTypography;
  if (options.isSmallMobile) {
    base = {
      ...tokens.typography[level],
      size: CONTENT_BLOCK_SMALL_MOBILE_HEADING_SIZE[level],
    };
  } else if (options.isMobile) {
    base = level === 'h4' ? tokens.typography.h4 : tokens.typography.mobile[level];
  } else {
    base = tokens.typography[level];
  }

  return { ...base, lineHeight: resolveHeadingLineHeight(base.size) };
}
